// Словарь и мелкие расчёты показа для раздела «Личный кабинет».
//
// ЧЕГО ЗДЕСЬ НЕТ НАМЕРЕННО — ЭТО ВАЖНЕЕ ТОГО, ЧТО ЕСТЬ.
//
// 1. НИ ОДНОЙ ДЕНЕЖНОЙ ФОРМУЛЫ. Остаток, суммы начислений и итоги считает ядро
//    (profile_api.py), причём вызовом тех же ручек, из которых их видит владелец в
//    «Ведомости инженера». Вторая реализация «доступно = заработано − выплачено» на
//    фронте однажды разойдётся с первой, и человек увидит в кабинете один остаток, а
//    владелец в ведомости другой. Кто прав, выяснять будет некому.
//
// 2. НИ НАЗВАНИЯ УРОВНЯ, НИ ЕГО МНОЖИТЕЛЯ, НИ ТАРИФОВ. На портале 73 это константы кода
//    (`LEVELS` и `POINTS` в src/gamif.js), в зеркало они не переносятся, и придумать их
//    здесь значило бы напечатать человеку неверную ставку за его работу. Кабинет
//    показывает номер уровня и говорит словами, чего он не знает. Оговорку про это
//    присылает сервер (`notes`), а не хранит этот файл: текст, живущий на экране,
//    рассказывает про зеркало то, что было верно в день, когда его писали.
//
// 3. НИ ОДНОГО ВБИТОГО ЧИСЛА. Порогов, ставок и лимитов здесь нет — всё, что показывается
//    числом, приезжает из ответа.

import type {
  ДеньгиКабинета, РаботаКабинета, СвежестьКабинета, СерииКабинета, СводкаКабинета,
} from '../api/profile'
import { plural } from './format'

/** Та же страница, что PAGE в profile_api.py. */
export const РАЗМЕР_СТРАНИЦЫ = 50

/** Портал 73: там подают заявку на выплату и там же живёт согласование. */
export const ПОРТАЛ_73_КАБИНЕТ = 'https://73.cirspb.com/#/profile'
export const ПОРТАЛ_73_БАЛАНС = 'https://73.cirspb.com/#/balance'

// ───────────────────────────── числа и слова ─────────────────────────────

/** Число с разрядами: 33825 → «33 825». Пусто — прочерк, а не ноль. */
export function число(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—'
  return Number(n).toLocaleString('ru-RU', { maximumFractionDigits: 2 })
}

/**
 * «33 825 ₽». Прочерк, если суммы нет: ноль и «неизвестно» — разные ответы.
 *
 * Балл портала равен рублю (курс POINTS_RUB_RATE у портала не задан, умолчание 1), и
 * поэтому баллы показываются рублями. Слово «баллы» на экране кабинета не употребляется
 * вовсе: человек получает деньги, и называть их баллами — приукрашивание.
 */
export function рубли(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—'
  return `${число(n)} ₽`
}

/** «34 верификации», «1 верификация», «2 верификации». Разряды и склонение вместе. */
export function верификации(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—'
  return `${число(n)} ${plural(Number(n), 'верификация', 'верификации', 'верификаций')}`
}

/** «9 отчётов», «1 отчёт», «2 отчёта». */
export function отчёты(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—'
  return `${число(n)} ${plural(Number(n), 'отчёт', 'отчёта', 'отчётов')}`
}

/** «32 бейджа», «1 бейдж», «2 бейджа». */
export function бейджи(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—'
  return `${число(n)} ${plural(Number(n), 'бейдж', 'бейджа', 'бейджей')}`
}

/** «20 инициатив», «1 инициатива», «2 инициативы». */
export function инициативы(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—'
  return `${число(n)} ${plural(Number(n), 'инициатива', 'инициативы', 'инициатив')}`
}

/** «5 заданий», «1 задание», «2 задания». */
export function задания(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—'
  return `${число(n)} ${plural(Number(n), 'задание', 'задания', 'заданий')}`
}

/** «8 записей», «1 запись», «2 записи» — для овертаймов и командировок. */
export function записи(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—'
  return `${число(n)} ${plural(Number(n), 'запись', 'записи', 'записей')}`
}

/** «4 выплаты», «1 выплата», «5 выплат». */
export function выплаты(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—'
  return `${число(n)} ${plural(Number(n), 'выплата', 'выплаты', 'выплат')}`
}

/** «3 командировки», «1 командировка». */
export function командировки(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—'
  return `${число(n)} ${plural(Number(n), 'командировка', 'командировки', 'командировок')}`
}

/** «28 дней подряд», «1 день подряд». Серия — про дни, а не про «раз». */
export function серияДней(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—'
  const к = Number(n)
  return `${число(к)} ${plural(к, 'день', 'дня', 'дней')} подряд`
}

/**
 * «7.5» → «7 ч 30 мин». Дробные часы на экране про заработок читаются плохо: «2.75 ч»
 * человек делит в голове, и делит по-разному.
 */
export function часыСловами(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—'
  const всего = Math.round(Number(v) * 60)
  const ч = Math.trunc(всего / 60)
  const м = всего % 60
  if (!ч && !м) return '—'
  const части: string[] = []
  if (ч) части.push(`${число(ч)} ч`)
  if (м) части.push(`${м} мин`)
  return части.join(' ')
}

// ───────────────────────────── уровень ─────────────────────────────

/**
 * Уровень словом — ровно в той мере, в какой он известен зеркалу, то есть номером.
 *
 * Названия уровней («Стажёр», «Инженер», «Мастер») и множители к ставке лежат
 * константами в коде портала и в зеркало не переносятся. Поэтому здесь «уровень 1»,
 * а не «Инженер ×1.10»: подписать чужую ставку своей догадкой — это про деньги соврать.
 */
export function уровеньСловами(level: number | null | undefined): string {
  if (level === null || level === undefined || Number.isNaN(Number(level))) {
    return 'уровень неизвестен'
  }
  return `уровень ${число(level)}`
}

// ───────────────────────────── состояния и признаки ─────────────────────────────

/** Вид начисления → как это назвать человеку. Незнакомое НЕ прячем, а показываем кодом. */
const ВИДЫ_НАЧИСЛЕНИЙ: Record<string, string> = {
  badge: 'бейдж',
  verif_short: 'короткая верификация',
  verif_full: 'полная верификация',
  gratitude: 'благодарность заказчика',
  job_report: 'отчёт по задаче',
  other: 'формулировку разобрать не удалось',
}

/**
 * Название вида начисления. Незнакомый код показывается как есть: спрятать его значило бы
 * сказать «такого начисления не было», а оно было — просто портал поменял формулировку.
 */
export function видНачисления(kind: string | null | undefined): string {
  const к = String(kind ?? '').trim()
  if (!к) return 'вид не указан'
  return ВИДЫ_НАЧИСЛЕНИЙ[к] ?? к
}

/** Есть ли что-то, о чём человеку стоит знать: расхождение баланса, неразобранный вид. */
export function расхождениеБаланса(д: ДеньгиКабинета | undefined): number {
  const v = Number(д?.total_minus_log ?? 0)
  return Number.isNaN(v) ? 0 : v
}

/**
 * Самый отставший источник кабинета. Не средний и не первый: доверять данным можно
 * настолько, насколько свеж САМЫЙ старый из них.
 */
export function свежесть(
  список: СвежестьКабинета[] | null | undefined,
): { entity: string; age_min: number } | null {
  const строки = (список ?? []).filter((r) => r && r.age_min !== null
                                              && r.age_min !== undefined)
  if (!строки.length) return null
  let худшая = строки[0]
  for (const r of строки) {
    if (Number(r.age_min) > Number(худшая.age_min)) худшая = r
  }
  return { entity: String(худшая.entity ?? ''), age_min: Number(худшая.age_min) }
}

/** Имена источников кабинета человеческими словами. Незнакомый — как есть. */
export const ИМЯ_СУЩНОСТИ: Record<string, string> = {
  'p73:users': 'учётки инженеров',
  'p73:verifications': 'верификации судов',
  'p73:job_reports': 'отчёты по заявкам',
  'p73:equip_acts': 'акты на оборудование',
  'p73g:points': 'баланс баллов',
  'p73g:bonus_log': 'журнал начислений',
  'p73g:payouts': 'выплаты',
  'p73g:payout_requests': 'заявки на выплату',
  'p73g:badges': 'каталог бейджей',
  'p73g:user_badges': 'выданные бейджи',
  'p73i:deals': 'инициативы',
  'p73x:overtime': 'овертаймы',
  'p73x:travel': 'командировки',
  'bp:tasks': 'задания процессов',
}

/**
 * Есть ли в кабинете хоть что-нибудь. Нужно, чтобы отличить «человек ещё не работал» от
 * «раздел не собрал данные»: первое — правда и её надо сказать словами, второе — поломка.
 */
export function кабинетПустой(с: СводкаКабинета | undefined): boolean {
  if (!с || !с.found) return false
  const р = с.work
  const д = с.money
  return !Number(д?.total_points ?? 0)
    && !Number(р?.verif_total ?? 0)
    && !Number(р?.reports_total ?? 0)
    && !Number(р?.acts_total ?? 0)
    && !Number(р?.bp_live ?? 0)
    && !Number(с.overtime_n ?? 0)
    && !Number(с.trips_n ?? 0)
    && !Number(с.initiatives?.initiatives ?? 0)
}

/**
 * Что из работы стоит показать плитками, а что скрыть. Пустая графа — не ноль, который
 * надо напечатать: «актов 0» у человека, который их никогда не делал, занимает место
 * и ничего не сообщает. А вот «верификаций 0» у инженера — сообщает, поэтому графы
 * работы показываются всегда, а необязательные (акты, задания) — только когда есть.
 */
export function графыРаботы(р: РаботаКабинета | undefined): Array<[string, number]> {
  if (!р) return []
  const всегда: Array<[string, number]> = [
    ['Верификации', Number(р.verif_total ?? 0)],
    ['Одобрено', Number(р.verif_approved ?? 0)],
  ]
  const по_наличию: Array<[string, number]> = [
    ['Отчёты по заявкам', Number(р.reports_total ?? 0)],
    ['Акты на оборудование', Number(р.acts_total ?? 0)],
    ['Задания процессов', Number(р.bp_live ?? 0)],
  ]
  return [...всегда, ...по_наличию.filter(([, n]) => n > 0)]
}

/** Есть ли у человека незакрытая работа — то, что он увидит первым делом. */
export function незакрытое(р: РаботаКабинета | undefined): number {
  if (!р) return 0
  return Number(р.verif_open ?? 0) + Number(р.reports_open ?? 0)
}

/**
 * Серия сгорит? Портал роняет уровень при простое, но ПОРОГ простоя — константа его
 * кода, в зеркало она не переносится. Поэтому здесь только «когда была последняя
 * активность», без обещания «до деградации осталось N дней»: назвать срок, не зная
 * порога, значит напугать или успокоить наугад.
 */
export function последняяАктивность(
  с: СерииКабинета | undefined, р: РаботаКабинета | undefined,
): string | null {
  const даты = [с?.last_login_at, р?.verif_last_at].filter(Boolean) as string[]
  if (!даты.length) return null
  // `Array.at(-1)` не годится: tsconfig тестов на es2021, и метода там нет.
  const по_возрастанию = [...даты].sort()
  return по_возрастанию[по_возрастанию.length - 1] ?? null
}
